// Formulaire de demande d'achat (tout utilisateur) : besoin, urgence, articles et quantités.
import React, { useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Grid, IconButton, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { AddOutlined, DeleteOutline, SendOutlined } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Panel } from '../../components/ui';
import { daAPI } from '../../services/api';
import { apiError } from '../../utils/format';

const UNITES = ['unité', 'kg', 't', 'sac', 'm', 'm²', 'm³', 'l', 'barre', 'rouleau', 'carton', 'lot'];
const vide = () => ({ intrant_id: null, designation: '', unite: 'unité', quantite: '', observation: '' });

const DemandeAchatForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [f, setF] = useState({ service: '', date_besoin: '', urgence: 'NORMALE', motif: '' });
  const [lignes, setLignes] = useState([vide()]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { daAPI.articles().then((r) => setArticles(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!id) return;
    daAPI.get(id).then((r) => {
      const d = r.data;
      setF({ service: d.service || '', date_besoin: d.date_besoin || '', urgence: d.urgence, motif: d.motif });
      setLignes(d.lignes.map((l) => ({ intrant_id: l.intrant_id, designation: l.designation, unite: l.unite, quantite: String(l.quantite).replace('.', ','), observation: l.observation || '' })));
    }).catch((e) => setError(apiError(e)));
  }, [id]);

  const setLigne = (i, patch) => setLignes((prev) => prev.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const ok = f.motif.trim() && lignes.length && lignes.every((l) => l.designation.trim() && Number(String(l.quantite).replace(',', '.')) > 0);

  const save = async () => {
    setBusy(true); setError('');
    try {
      const body = { ...f, lignes: lignes.map((l) => ({ ...l, quantite: String(l.quantite).replace(',', '.') })) };
      if (id) { await daAPI.update(id, body); navigate(`/demandes-achat/${id}`, { state: { info: 'Demande modifiée' } }); }
      else { const r = await daAPI.create(body); navigate(`/demandes-achat/${r.data.id}`, { state: { info: r.data.message } }); }
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Layout title={id ? "Modifier la demande d'achat" : "Nouvelle demande d'achat"} plain>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      <Panel title="Besoin" sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <TextField fullWidth size="small" label="Service / chantier" placeholder="ex. Atelier ferraillage, chantier Mbour"
              value={f.service} onChange={(e) => setF({ ...f, service: e.target.value })} />
          </Grid>
          <Grid item xs={7} md={4}>
            <TextField fullWidth size="small" type="date" label="Besoin pour le" InputLabelProps={{ shrink: true }}
              value={f.date_besoin} onChange={(e) => setF({ ...f, date_besoin: e.target.value })} />
          </Grid>
          <Grid item xs={5} md={3}>
            <ToggleButtonGroup fullWidth size="small" exclusive value={f.urgence} onChange={(e, v) => v && setF({ ...f, urgence: v })} aria-label="Urgence">
              <ToggleButton value="NORMALE">Normale</ToggleButton>
              <ToggleButton value="URGENTE" sx={{ '&.Mui-selected': { bgcolor: '#F8E1E2', color: '#B4232A' } }}>Urgente</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline minRows={2} size="small" required label="Motif / justification"
              placeholder="À quoi servira cet achat ? ex. Remplacement du disque de la tronçonneuse, stock de fer 12 presque épuisé…"
              value={f.motif} onChange={(e) => setF({ ...f, motif: e.target.value })} />
          </Grid>
        </Grid>
      </Panel>

      <Panel title="Articles demandés" flush sx={{ mb: 2 }}
        action={<Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Choisissez dans la liste ou tapez librement</Typography>}>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '42%' }}>Article</TableCell><TableCell sx={{ width: 110 }}>Quantité</TableCell>
                <TableCell sx={{ width: 130 }}>Unité</TableCell><TableCell>Précision (marque, dimension…)</TableCell><TableCell sx={{ width: 48 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {lignes.map((l, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Autocomplete freeSolo size="small" options={articles}
                      getOptionLabel={(o) => (typeof o === 'string' ? o : o.designation)}
                      value={l.intrant_id ? articles.find((a) => a.id === l.intrant_id) || l.designation : l.designation}
                      onChange={(e, v) => (v && typeof v === 'object'
                        ? setLigne(i, { intrant_id: v.id, designation: v.designation, unite: v.unite })
                        : setLigne(i, { intrant_id: null, designation: v || '' }))}
                      onInputChange={(e, v, reason) => { if (reason === 'input') setLigne(i, { intrant_id: null, designation: v }); }}
                      renderOption={(props, o) => <li {...props} key={o.id}>{o.designation}<Typography component="span" sx={{ ml: 1, fontSize: 12, color: 'text.secondary' }}>{o.unite}</Typography></li>}
                      renderInput={(p) => <TextField {...p} variant="standard" placeholder="ex. Fer de 12, gants, disque…" inputProps={{ ...p.inputProps, 'aria-label': `Article ${i + 1}` }} />} />
                  </TableCell>
                  <TableCell>
                    <TextField variant="standard" value={l.quantite} inputProps={{ inputMode: 'decimal', 'aria-label': `Quantité ${i + 1}`, style: { textAlign: 'right' } }}
                      onChange={(e) => setLigne(i, { quantite: e.target.value })} />
                  </TableCell>
                  <TableCell>
                    <TextField select variant="standard" fullWidth value={UNITES.includes(l.unite) ? l.unite : l.unite || 'unité'} disabled={!!l.intrant_id}
                      onChange={(e) => setLigne(i, { unite: e.target.value })}>
                      {[...new Set([...UNITES, l.unite].filter(Boolean))].map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell><TextField variant="standard" fullWidth value={l.observation} onChange={(e) => setLigne(i, { observation: e.target.value })} /></TableCell>
                  <TableCell>
                    <Tooltip title="Retirer"><span>
                      <IconButton size="small" disabled={lignes.length === 1} aria-label={`Retirer la ligne ${i + 1}`} onClick={() => setLignes(lignes.filter((_, j) => j !== i))}><DeleteOutline fontSize="small" /></IconButton>
                    </span></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <Box sx={{ p: 1.5 }}><Button startIcon={<AddOutlined />} onClick={() => setLignes([...lignes, vide()])}>Ajouter un article</Button></Box>
      </Panel>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
        <Button onClick={() => navigate(-1)}>Annuler</Button>
        <Button variant="contained" startIcon={<SendOutlined />} disabled={!ok || busy} onClick={save}>
          {id ? 'Enregistrer' : 'Envoyer la demande'}
        </Button>
      </Box>
    </Layout>
  );
};

export default DemandeAchatForm;
